import React, { useState } from 'react';
import { Phone, MapPin, Clock, CheckCircle, Truck, Package, Home, Building2, Flower2, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast.success('Quote request received! We will contact you within 1 hour.');
    setFormData({ name: '', email: '', phone: '', serviceType: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_00e0f45b-6b4a-49ad-84b9-65dc71518ea4/artifacts/f30279pp_IMG_3958.jpeg" 
              alt="A & R Enterprise" 
              className="h-12 w-12 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">A & R ENTERPRISE</h1>
              <p className="text-sm text-gray-600">Tampa, Florida</p>
            </div>
          </div>
          <a href="tel:8134176390">
            <Button className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white">
              <Phone className="h-4 w-4 mr-2" />
              (813) 417-6390
            </Button>
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8f5ff] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Reliable 26' Box Truck with Liftgate
                <span className="block text-[#7c3aed] mt-2">Immediate Availability</span>
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Professional logistics services for brokers and coordinators across Florida. 
                Commercial freight, residential moves, and specialized transport solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href="#quote">
                  <Button size="lg" className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-lg px-8 py-6">
                    Get Free Quote
                  </Button>
                </a>
                <a href="tel:8134176390">
                  <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-[#7c3aed] text-[#7c3aed] hover:bg-[#f8f5ff]">
                    <Phone className="h-5 w-5 mr-2" />
                    Call Now
                  </Button>
                </a>
              </div>
              <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[#7c3aed]" />
                  <span>Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[#7c3aed]" />
                  <span>DOT Certified</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-[#7c3aed]" />
                  <span>Same-Day Available</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_00e0f45b-6b4a-49ad-84b9-65dc71518ea4/artifacts/9990epj9_IMG_3962.jpeg"
                alt="A & R Enterprise Box Truck"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Complete Logistics Solutions</h3>
            <p className="text-xl text-gray-600">Specialized services for brokers and freight coordinators</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-[#7c3aed] transition-all hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="h-14 w-14 bg-[#f8f5ff] rounded-xl flex items-center justify-center mb-4">
                  <Flower2 className="h-8 w-8 text-[#7c3aed]" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Pallet Transport</h4>
                <p className="text-gray-600">
                  Specialized flower pallet delivery to warehouses and distribution centers. 
                  Temperature-sensitive cargo handling.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#7c3aed] transition-all hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="h-14 w-14 bg-[#f8f5ff] rounded-xl flex items-center justify-center mb-4">
                  <Wrench className="h-8 w-8 text-[#7c3aed]" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Appliance Transport</h4>
                <p className="text-gray-600">
                  Safe transportation of appliances with liftgate equipment. 
                  White glove service for deliveries.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#7c3aed] transition-all hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="h-14 w-14 bg-[#f8f5ff] rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="h-8 w-8 text-[#7c3aed]" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Commercial Freight</h4>
                <p className="text-gray-600">
                  Business-to-business freight services. Warehouse transfers and 
                  distribution facility deliveries.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#7c3aed] transition-all hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="h-14 w-14 bg-[#f8f5ff] rounded-xl flex items-center justify-center mb-4">
                  <Home className="h-8 w-8 text-[#7c3aed]" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Residential Moves</h4>
                <p className="text-gray-600">
                  Household items and furniture moving. Local and long-distance 
                  residential services across Florida.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#7c3aed] transition-all hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="h-14 w-14 bg-[#f8f5ff] rounded-xl flex items-center justify-center mb-4">
                  <Package className="h-8 w-8 text-[#7c3aed]" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">General Freight</h4>
                <p className="text-gray-600">
                  Full truckload and LTL shipments. Flexible scheduling for urgent 
                  and standard deliveries.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#7c3aed] transition-all hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="h-14 w-14 bg-[#f8f5ff] rounded-xl flex items-center justify-center mb-4">
                  <Truck className="h-8 w-8 text-[#7c3aed]" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Distribution Services</h4>
                <p className="text-gray-600">
                  Shipping facility transfers and coordinated multi-stop routes. 
                  Reliable partner for logistics coordinators.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8f5ff] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Statewide Florida Coverage</h3>
            <p className="text-xl text-gray-600">Based in Tampa, serving all of Florida</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="aspect-[4/3] bg-gradient-to-br from-[#f0e7ff] to-[#e0d4ff] rounded-xl flex items-center justify-center mb-6">
                <div className="text-center">
                  <MapPin className="h-16 w-16 text-[#7c3aed] mx-auto mb-4" />
                  <div className="text-2xl font-bold text-gray-900">FLORIDA</div>
                  <div className="text-lg text-gray-600 mt-2">Statewide Service</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-6 w-6 text-[#7c3aed] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900">Headquarters: Tampa Bay Area</div>
                    <div className="text-gray-600 text-sm">8913 Regents Park Dr STE 681, Tampa, FL 33647</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Truck className="h-6 w-6 text-[#7c3aed] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900">Coverage: All Major Florida Markets</div>
                    <div className="text-gray-600 text-sm">Miami, Orlando, Jacksonville, Fort Myers, and everywhere in between</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-2xl font-bold text-gray-900 mb-6">Why Work With Us?</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <CheckCircle className="h-6 w-6 text-[#7c3aed] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">No Load Board Competition</div>
                    <div className="text-gray-600">Direct partnership means better rates and reliable service</div>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <CheckCircle className="h-6 w-6 text-[#7c3aed] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Same-Day & Next-Day Available</div>
                    <div className="text-gray-600">Quick response for urgent freight needs</div>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <CheckCircle className="h-6 w-6 text-[#7c3aed] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">26' Box Truck with Liftgate</div>
                    <div className="text-gray-600">Professional equipment for all cargo types</div>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm">
                  <CheckCircle className="h-6 w-6 text-[#7c3aed] mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Licensed & Insured</div>
                    <div className="text-gray-600">DOT certified with full cargo insurance</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Get Your Free Quote</h3>
            <p className="text-xl text-gray-600">Contact us for immediate availability and competitive rates</p>
          </div>

          <Card className="shadow-2xl border-2">
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Your Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      required
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Phone Number *</label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Email Address *</label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Service Type *</label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
                  >
                    <option value="">Select a service</option>
                    <option value="pallet">Pallet Transport</option>
                    <option value="appliance">Appliance Transport</option>
                    <option value="commercial">Commercial Freight</option>
                    <option value="residential">Residential Move</option>
                    <option value="general">General Freight</option>
                    <option value="distribution">Distribution Services</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Additional Details</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your freight needs, pickup/delivery locations, timeline, etc."
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-lg py-6">
                  Get Free Quote
                </Button>

                <div className="text-center pt-4">
                  <p className="text-gray-600 mb-3">Or call us directly:</p>
                  <a href="tel:8134176390" className="text-3xl font-bold text-[#7c3aed] hover:text-[#6d28d9]">
                    (813) 417-6390
                  </a>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_00e0f45b-6b4a-49ad-84b9-65dc71518ea4/artifacts/f30279pp_IMG_3958.jpeg" 
                  alt="A & R Enterprise" 
                  className="h-10 w-10 object-contain"
                />
                <div>
                  <div className="font-bold text-lg">A & R ENTERPRISE</div>
                  <div className="text-sm text-gray-400">Reliable Movers, Happy Customers, Built on Trust</div>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-bold text-lg mb-4">Contact</h5>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <a href="tel:8134176390" className="hover:text-[#7c3aed]">(813) 417-6390</a>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>8913 Regents Park Dr, Tampa, FL</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-bold text-lg mb-4">Service Hours</h5>
              <div className="space-y-1 text-gray-300 text-sm">
                <div>Sunday - Tuesday: 24 Hours</div>
                <div>Wednesday: Closed</div>
                <div>Thursday: Closed</div>
                <div>Friday: Closed</div>
                <div>Saturday: Closed</div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 A & R ENTERPRISE. All rights reserved. | Licensed & Insured | DOT Certified</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
